import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { Alert, Box, Button, Checkbox, FormControlLabel } from '@mui/material';

import { formatDateISO, startOfMonth } from '~/services/TimeService';
import VendorsService from '~/services/VendorsService';

import ErrorDisplay from '../ErrorDisplay';

import AutocompleteWidget from './widgets/AutocompleteWidget';

function validateForm(formData, errors) {
  if (typeof formData.dateOfPayment === 'undefined') {
    errors.dateOfPayment.addError('Please enter date of payment');
  }

  if (typeof formData.fromCurrency === 'undefined') {
    errors.fromCurrency.addError('Please select a currency');
  }

  if (typeof formData.toCurrency === 'undefined') {
    errors.toCurrency.addError('Please select a currency');
  }

  if (typeof formData.sellAccount === 'undefined') {
    errors.sellAccount.addError('Please select a sell account');
  }

  return errors;
}

const CURRENCIES = [
  'AUD',
  'EUR',
  'GBP',
  'HKD',
  'IDR',
  'INR',
  'JPY',
  'KRW',
  'MAD',
  'MYR',
  'NZD',
  'QAR',
  'SGD',
  'THB',
  'USD',
  'VND',
];

const SELL_ACCOUNTS = ['SGD', 'AUD'];

const formSchema: RJSFSchema = {
  description: 'Please enter a date of payment and select a currency to generate a CBA INT file',
  type: 'object',
  properties: {
    dateOfPayment: {
      title: 'Date of payment',
      type: 'string',
      format: 'date',
      default: formatDateISO(startOfMonth()),
    },
    fromCurrency: {
      title: 'From currency',
      type: 'string',
      enum: CURRENCIES,
    },
    toCurrency: {
      title: 'To currency',
      type: 'string',
      enum: CURRENCIES,
    },
    sellAccount: {
      title: 'Sell account',
      type: 'string',
      enum: SELL_ACCOUNTS,
      default: 'SGD',
    },
  },
};

const uiSchema = {
  dateOfPayment: {
    'ui:widget': 'date',
  },
  fromCurrency: {
    'ui:widget': 'AutocompleteWidget',
  },
  toCurrency: {
    'ui:widget': 'AutocompleteWidget',
  },
  sellAccount: {
    'ui:widget': 'AutocompleteWidget',
  },
};

const widgets = {
  AutocompleteWidget,
};

export default function CBAINTFileForm() {
  const [error, setError] = useState(null);
  const [comment, setComment] = useState(null);
  const [formData, setFormData] = useState({ sellAccount: 'SGD' });
  const [filterByUser, setFilterByUser] = useState(false);

  const handleSubmit = async (event: IChangeEvent) => {
    try {
      setComment('Downloading CBA INT File...');

      await VendorsService.downloadCBAINTFile(
        event.formData.dateOfPayment,
        event.formData.fromCurrency,
        event.formData.toCurrency,
        event.formData.sellAccount,
        filterByUser || undefined,
      );

      setComment('CBA INT File downloaded successfully.');
      setError(null);
    } catch (err) {
      setComment(null);
      setError(err.message);
    }
  };

  const handleChange = (event: IChangeEvent) => {
    setFormData(event.formData);
  };

  return (
    <Form
      schema={formSchema}
      uiSchema={uiSchema}
      formData={formData}
      onChange={handleChange}
      onSubmit={handleSubmit}
      customValidate={validateForm}
      validator={validator}
      widgets={widgets}
      showErrorList={false}
    >
      <Box mb={2}>
        <FormControlLabel
          label="Only include my remittances"
          control={<Checkbox checked={filterByUser} onChange={(e) => setFilterByUser(e.target.checked)} />}
        />
      </Box>

      <Button type="submit" variant="contained">
        Download CBA INT File
      </Button>

      {error && <ErrorDisplay message={error} />}

      {comment && (
        <Alert severity="info">
          <p>{comment}</p>
        </Alert>
      )}
    </Form>
  );
}
